/**
 * Per-project access roles of the workspace's logins.
 * Contract: alfa2267/ans-backend routes/planning/projectMembers.ts (docs/plans/2026-10-30-project-member-roles.md).
 * Routes are per project, so every call takes the project explicitly. No row for a login = the workspace default applies.
 */
import { apiDelete, apiGet, apiPut } from './PlanningApiClient';
import type { UserRole } from '../types/User';

/** What a project role can be set to. `none` shuts the person out of that project. */
export type ProjectAccessRole = Exclude<UserRole, 'owner'> | 'none';

export interface ProjectMember {
  user_id: string;
  email: string | null;
  name: string | null;
  /** The login's role for the whole workspace. */
  workspace_role: UserRole | null;
  /** The role set on this project; null when the workspace role applies. */
  project_role: ProjectAccessRole | null;
  /** What the person actually holds on this project. */
  effective_role: string | null;
}

const base = (projectId: string) => `/planning/${encodeURIComponent(projectId)}/members`;

/** Every live login of the project's workspace with the role it holds on this project. Admin on the project only. */
export async function fetchProjectMembers(projectId: string): Promise<ProjectMember[]> {
  const res = await apiGet<{ data?: ProjectMember[] } | null>(base(projectId));
  return res?.data ?? [];
}

export function setProjectRole(projectId: string, userId: string, role: ProjectAccessRole): Promise<unknown> {
  return apiPut<unknown>(`${base(projectId)}/${encodeURIComponent(userId)}`, { role });
}

/** Takes the project role away; the person falls back to their workspace role. */
export function clearProjectRole(projectId: string, userId: string): Promise<unknown> {
  return apiDelete<unknown>(`${base(projectId)}/${encodeURIComponent(userId)}`);
}
